import { useCallback, useMemo, useState } from 'react';
import type {
  ActionConfirmationModalProps,
  ConfirmationModalContent,
  ConfirmationModalSeverity,
  UseActionConfirmationModalResult,
} from './types';

type ActionConfirmationModalState = {
  isOpen: boolean;
  content: ConfirmationModalContent | null;
  severity: ConfirmationModalSeverity | null;
  isLoading: boolean;
  onConfirm: (() => void | Promise<void>) | null;
};

const INITIAL_STATE: ActionConfirmationModalState = {
  isOpen: false,
  content: null,
  severity: null,
  isLoading: false,
  onConfirm: null,
};

export function useActionConfirmationModal(): UseActionConfirmationModalResult {
  const [state, setState] = useState<ActionConfirmationModalState>(INITIAL_STATE);

  const closeConfirmationModal = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const openConfirmationModal = useCallback(
    (
      content: ConfirmationModalContent,
      severity: ConfirmationModalSeverity,
      onConfirm: () => void | Promise<void>,
    ) => {
      setState({
        isOpen: true,
        content,
        severity,
        isLoading: false,
        onConfirm,
      });
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (!state.onConfirm) return;

    const { onConfirm } = state;
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      await onConfirm();
      setState(INITIAL_STATE);
    } catch {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [state.onConfirm]);

  const modalProps = useMemo<ActionConfirmationModalProps | null>(() => {
    if (!state.isOpen || !state.content || !state.severity) return null;

    return {
      isOpen: state.isOpen,
      severity: state.severity,
      content: state.content,
      isLoading: state.isLoading,
      onClose: closeConfirmationModal,
      onConfirm: handleConfirm,
    };
  }, [closeConfirmationModal, handleConfirm, state]);

  return {
    openConfirmationModal,
    closeConfirmationModal,
    modalProps,
  };
}
